import { useRef } from 'react';
import { RequestStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { ScheduleQueries, ScheduleQueryInvalidators } from '@frontend/api-schedule';
import { ScheduleV2 } from '@frontend/api-schedule-v2';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { ScheduleRequestListItem } from '@frontend/schedule-helpers';
import { useAlert } from '@frontend/design-system';

export type ScheduleRequestActionCalledFrom = 'request_details' | 'action_menu';
type ActionType = 'dismiss' | 'done' | 'message' | 'call' | 'delete' | 'reject';

export const useScheduleRequestActions = (listItem: ScheduleRequestListItem) => {
  const { t } = useTranslation('schedule-request');
  const alert = useAlert();

  const user = getUser();
  const userId = user?.userID ?? '';
  const locationId = listItem.locationId ?? '';
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const requestVersion = listItem.linkHash || '';
  const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;
  const scheduleRequestId = listItem.id ?? '';
  const sourceDetails = listItem.aiRequestDetails?.sourceDetails;
  const threadId = sourceDetails?.threadId || '';
  const requestSource = listItem.aiRequestDetails ? 'AI' : 'online_scheduling';
  const dismissActionCalledFromRef = useRef<ScheduleRequestActionCalledFrom>('action_menu');

  const { invalidateScheduleRequestListAndCountV2 } =
    ScheduleQueryInvalidators.useScheduleRequestListAndCountInvalidation();
  const { navigateToThread } = useInboxNavigate();

  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();
  const { handleCallActionClick } = ActionsUI.actions.usePhoneCallAction({
    context: {
      personId: listItem.schedulee?.weavePersonId ?? '',
      phoneNumber: listItem.schedulee?.phoneNumber ?? '',
      locationId,
    },
  });

  const deleteV3RequestMutation = SchedulerV3.Mutations.useDeleteBookingSubmissionMutation();
  const deleteV2RequestMutation = ScheduleQueries.useDeleteScheduleRequest();
  const updateV3RequestMutation = SchedulerV3.Mutations.useUpdateBookingSubmissionMutation();
  const updateV2RequestMutation = ScheduleV2.Mutations.useUpdateScheduleRequestMutation();

  const updateV3RequestStatus = async (reviewedStatus: ReviewedStatus) => {
    await updateV3RequestMutation.mutateAsync({
      submissionId: scheduleRequestId,
      reviewedBy: userId,
      reviewedStatus,
    });
  };

  const updateV2RequestStatus = async (status: RequestStatus) => {
    await updateV2RequestMutation.mutateAsync({
      id: listItem.id,
      reviewedBy: userId,
      status,
    });
    invalidateScheduleRequestListAndCountV2();
  };

  const trackConversationLinkClick = (sourceType: 'sms' | 'phone') => {
    SchedulerV3.trackPendoEvent('air_request_conversation_link_click', {
      appointmentRequestId: scheduleRequestId,
      sourceType,
      requestSource,
      userId,
      locationId: listItem.locationId ?? '',
      threadId: sourceDetails?.threadId || '',
      messageId: sourceDetails?.messageId || '',
      channelId: sourceDetails?.channelId || '',
    });
  };

  const trackRequestAction = (
    actionType: ActionType,
    actionCalledFrom: ScheduleRequestActionCalledFrom = 'action_menu'
  ) => {
    SchedulerV3.trackPendoEvent('request_action_click', {
      appointmentRequestId: scheduleRequestId,
      userId,
      locationId: listItem.locationId ?? '',
      actionCalledFrom,
      actionType,
      requestSource,
    });
  };

  const setDismissActionCalledFrom = (actionCalledFrom: ScheduleRequestActionCalledFrom = 'action_menu') => {
    dismissActionCalledFromRef.current = actionCalledFrom;
  };

  const handleMessage = async (
    updateStatus?: boolean,
    actionCalledFrom: ScheduleRequestActionCalledFrom = 'action_menu'
  ) => {
    try {
      trackRequestAction('message', actionCalledFrom);
      await messageOnClick({
        personId: listItem.schedulee?.weavePersonId,
        personPhone: listItem.schedulee?.phoneNumber ?? '',
        groupIds: [locationId],
        threadId,
      });

      // Update status should only be triggered for preference based requests
      if (updateStatus) {
        if (shouldUseV3) {
          await updateV3RequestStatus(ReviewedStatus.MESSAGED);
        } else {
          await updateV2RequestStatus(RequestStatus.MESSAGED);
        }
      }
    } catch (error) {
      alert.error(t('Failed to send message to patient'));
      throw error;
    }
  };

  const handlePhoneCall = async (actionCalledFrom: ScheduleRequestActionCalledFrom = 'action_menu') => {
    try {
      trackRequestAction('call', actionCalledFrom);
      handleCallActionClick();

      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.CALLED);
      } else {
        await updateV2RequestStatus(RequestStatus.CALLED);
      }
    } catch (error) {
      alert.error(t('Failed to make phone call to patient'));
      throw error;
    }
  };

  const handleDeleteRequest = async () => {
    try {
      trackRequestAction('delete', 'action_menu');
      if (shouldUseV3) {
        await deleteV3RequestMutation.mutateAsync({ submissionIds: [scheduleRequestId] });
      } else {
        await deleteV2RequestMutation.mutateAsync({ id: scheduleRequestId, locationId });
        invalidateScheduleRequestListAndCountV2();
      }
      alert.success(t('Appointment request deleted successfully'));
    } catch (error) {
      alert.error(t('Failed to delete appointment request'));
      throw error;
    }
  };

  const handleRejectRequest = async () => {
    try {
      trackRequestAction('reject', 'action_menu');
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.REJECTED);
      } else {
        throw new Error('Rejecting appointment requests is only supported for V3 requests');
      }
      alert.success(t('Appointment request rejected successfully'));
    } catch (error) {
      alert.error(t('Failed to reject appointment request'));
      throw error;
    }
  };

  const handleDismissRequest = async () => {
    const actionCalledFrom = dismissActionCalledFromRef.current;

    try {
      trackRequestAction('dismiss', actionCalledFrom);
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.DISMISSED);
      } else {
        await updateV2RequestStatus(RequestStatus.DISMISSED);
      }
      alert.success(t('Appointment request dismissed successfully'));
    } catch (error) {
      alert.error(t('Failed to dismiss appointment request'));
      throw error;
    } finally {
      dismissActionCalledFromRef.current = 'action_menu';
    }
  };

  const handleCompleteRequest = async (actionCalledFrom: ScheduleRequestActionCalledFrom = 'action_menu') => {
    try {
      trackRequestAction('done', actionCalledFrom);
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.DONE);
      } else {
        await updateV2RequestStatus(RequestStatus.DONE);
      }
      alert.success(t('Appointment request completed successfully'));
    } catch (error) {
      alert.error(t('Failed to complete appointment request'));
      throw error;
    }
  };

  const handleReviewCallLog = () => {
    const channelId = sourceDetails?.channelId || '';

    trackConversationLinkClick('phone');
    emitOpenCallInsightPanel({
      includeCallIntelligence: true,
      channelId,
      locationId,
    });
  };

  const handleViewConversation = async () => {
    const groupId = listItem.locationId;
    const messageId = sourceDetails?.messageId;

    if (threadId && groupId) {
      trackConversationLinkClick('sms');

      navigateToThread({
        groupId,
        threadId,
        personId: listItem.schedulee?.weavePersonId,
        personPhone: listItem.schedulee?.phoneNumber,
        threadItemId: messageId,
      });
      return;
    }

    await handleMessage(false);
  };

  return {
    isLoading:
      deleteV3RequestMutation.isLoading ||
      deleteV2RequestMutation.isLoading ||
      updateV3RequestMutation.isLoading ||
      updateV2RequestMutation.isLoading,
    handleMessage,
    handlePhoneCall,
    handleCompleteRequest,
    handleDeleteRequest,
    handleDismissRequest,
    setDismissActionCalledFrom,
    handleRejectRequest,
    handleReviewCallLog,
    handleViewConversation,
  };
};
